/* THINGS, PARTS AND EVENTS: the one small rule the simulation is built on
 * (LS-222, step one of the Caves of Qud plan; Vijay: "let's do 1-2 for sure").
 *
 * Every object in the world is a THING: a tile, a prop, a creature, an item,
 * a fire. A thing is a bag of PARTS, and a part is a piece of state with
 * handlers for EVENTS. Nothing is written for a pair of kinds; fire does not
 * know what a hedge is. A Fire part answers the tick by spending fuel and
 * sending 'heat' to what is beside it; a Burnable part answers 'heat' by
 * rolling to catch; a Wet part answers by refusing. Add a part to a thing and every event already knows it.
 *
 * `matter.ts` stays the table of what each kind IS; the parts here are read
 * off it, so a kind added to the table is a thing with the right parts
 * without another line. The rolls are the same seeded rolls `tickFire`
 * made, so a fire on a world burns the same way as it did before this
 * layer existed, and the check in the soak says so by running both. */
import { wearing } from './inventory.ts';
import { matterOf, tideKind } from '../matter.ts';
import type { Grid, Fire } from '../matter.ts';
import { mix } from '../rng.ts';
import { isBombable } from '../art.ts';
import { Fields } from './fields.ts';

export type Part = { name: string; [k: string]: any };
export type Thing = { id: number; kind: number; x: number; y: number; parts: Map<string, Part> };

let nextId = 1;
/** A new world starts its ids at one: the counter is per process, and two loads of one save in one tab drifted apart (the robustness lens, round 41). */
export function resetIds() { nextId = 1; }
export function thing(kind: number, x: number, y: number, parts: Part[] = []): Thing {
  const t: Thing = { id: nextId++, kind, x, y, parts: new Map() };
  for (const p of parts) t.parts.set(p.name, p);
  return t;
}
export const part = (t: Thing, name: string) => t.parts.get(name);
export const has = (t: Thing, name: string) => t.parts.has(name);

/* THE BUS. An event is a name, a target thing and a payload; every part of
 * the target that has a handler of that name answers, in the order the parts
 * were given. A handler may return a value; `emit` returns the last one that
 * was not undefined, so a question ('will you catch?') has an answer; and a
 * part that answers false ends the event, so a refusal is final. */
export type Ctx = { world: SimWorld; thing: Thing; payload: any };
export function emit(world: SimWorld, name: string, target: Thing, payload: any = {}) {
  let answer: any;
  for (const p of target.parts.values()) {
    const h = p['on' + name];
    if (typeof h !== 'function') continue;
    const r = h.call(p, { world, thing: target, payload } as Ctx);
    if (r === false) return false;   // A REFUSAL ENDS IT: a wet thing or a locked one says no to heat, and no later part gets to say yes
    if (r !== undefined) answer = r;
  }
  return answer;
}

/* THE WORLD THE THINGS LIVE IN: a grid of tile kinds (the game's own), the
 * clock, and the things standing on it. Tiles become things lazily, when
 * asked, with parts read off the matter table; a tile that changes kind is
 * a new thing next time it is asked. */
export class SimWorld {
  grid: Grid; seedNum: number; tick = 0; biomeDry = 0.6; day = 0.3; wind: [number, number] = [0, 0];
  fires: Thing[] = [];
  tileThings = new Map<string, Thing>();
  baseOf: ((x: number, y: number) => number | undefined) | null = null;   // what a tidal tile was at the world's making
  fields: Fields | null = null;   // heat, wet and growth per tile (LS-223); without them the rules count neighbours as before
  window: { x0: number; y0: number; x1: number; y1: number } | null = null;   // the tiles the fields tick in this step (LS-511): the world's field is ticked round the body, not whole
  constructor(grid: Grid, seedNum: number) { this.grid = grid; this.seedNum = seedNum; }
  tileAt(x: number, y: number): Thing | null {
    const v = this.grid.at(x, y);
    if (v === undefined) return null;
    const k = x + ',' + y;
    const had = this.tileThings.get(k);
    if (had && had.kind === v) return had;
    const t = thing(v, x, y, tileParts(v, this.grid, x, y, this.baseOf));
    this.tileThings.set(k, t);
    return t;
  }
  burning(x: number, y: number) { return this.fires.some((f) => f.x === x && f.y === y); }
  roll(x: number, y: number) { return ((mix(this.seedNum, this.tick * 31 + x, y * 17 + 3) >>> 8) & 1023) / 1024; }
}

/* THE PARTS A TILE HAS, from what it is. */
export function tileParts(kind: number, grid: Grid, x: number, y: number, baseOf: ((x: number, y: number) => number | undefined) | null = null): Part[] {
  const m = matterOf(kind);
  const parts: Part[] = [];
  if (kind === TIDAL_KINDS[0] || kind === TIDAL_KINDS[1]) parts.push(Tidal((baseOf && baseOf(x, y)) ?? kind));
  if (kind === BRAZIER_KIND) parts.push(Lightable());
  if (kind === NEST_KIND) parts.push(Nest());
  if (kind === THORN_KIND) parts.push(Cuttable());
  if (isBombable(kind)) parts.push(Breakable());
  if (m.chops > 0) parts.push(Choppable(m.chops));
  if (m.wet) parts.push(Wet());
  if (m.flammable > 0) parts.push(Burnable(m.flammable, m.fuel, m.ash, m.dry));
  if (m.brittle) parts.push({ name: 'Brittle' });
  if (m.heavy) parts.push({ name: 'Heavy' });
  if (m.lit) parts.push({ name: 'Lit' });
  if (m.shelter) parts.push({ name: 'Shelter' });
  if (m.regrows !== null) parts.push(Regrows(m.regrows));
  if (kind === PUSH_KIND) parts.push(Pushable());
  return parts;
}
export let BRAZIER_KIND = -1;
export let NEST_KIND = -1;
export function setNestKind(k: number) { NEST_KIND = k; }
/* A BLAST is an event too. Every thing in its reach is told 'blast'; a brazier lights, a nest breaks, a brittle
 * thing breaks, and what is left and can burn is heated as an ember would heat it. The first part to act marks
 * the payload handled, so a thing that is both brittle and burnable breaks rather than burns, as it did. */
export const Nest = (): Part => ({ name: 'Nest', onblast(c: Ctx) { if (c.payload.handled) return; c.payload.handled = true; c.payload.game.breakNestAt(c.thing.x, c.thing.y); return true; },
  oncut(c: Ctx) { c.payload.game.breakNestAt(c.thing.x, c.thing.y); return true; }, oncrush(c: Ctx) { c.payload.game.breakNestAt(c.thing.x, c.thing.y); return true; }, onpierce(c: Ctx) { c.payload.game.breakNestAt(c.thing.x, c.thing.y); return true; } });
/* THE WEAPONS' VERBS (LS-225 step 2). A strike is an event named for what the weapon does -- 'cut', 'pierce',
 * 'crush', 'burn' -- sent to the thing struck; its parts answer. A hedge is Cuttable (a blade clears it to
 * litter), a brittle thing is Breakable (an axe breaks it as a blast would), anything Burnable catches from a
 * torch, a nest breaks under any blade, a brazier lights from a torch. Nothing here names a weapon. */
export let THORN_CUT_INTO = -1;
export function setThornCutInto(k: number) { THORN_CUT_INTO = k; }
export const Cuttable = (): Part => ({ name: 'Cuttable', oncut(c: Ctx) { const g = c.payload.game; if (g && g.cutThornAt) g.cutThornAt(c.thing.x, c.thing.y); else c.world.grid.set(c.thing.x, c.thing.y, THORN_CUT_INTO); return true; } });
export const Breakable = (): Part => ({ name: 'Breakable', onblast(c: Ctx) { if (c.payload.handled) return; c.payload.handled = true; c.payload.game.breakTileAt(c.thing.x, c.thing.y); return true; },
  oncrush(c: Ctx) { c.payload.game.breakTileAt(c.thing.x, c.thing.y); return true; } });
/** FELLED BY THE AXE (LS-384; Vijay: "raw materials come out of the world's own systems"): a tree takes `n` crushing
 *  blows, chipping under each, and on the last comes down -- the tile is litter and two logs lie where it stood. */
export const Choppable = (n: number): Part => ({ name: 'Choppable',
  // the count lives on the game, not the thing: a blow builds its SimWorld afresh, so a thing remembers nothing
  oncrush(c: Ctx) { const t: any = c.thing, g = c.payload.game; if (g && g.chopTreeAt) g.chopTreeAt(t.x, t.y, n); return true; },
  // the sickle's edge takes twice the blows: the home island has no axe, and the first raft is cut with what you start with (LS-383)
  oncut(c: Ctx) { const t: any = c.thing, g = c.payload.game; if (g && g.chopTreeAt) g.chopTreeAt(t.x, t.y, n * 2); return true; },
});
export let THORN_KIND = -1;
export function setThornKind(k: number) { THORN_KIND = k; }
/** Regrows: scorched ground. Told the clock ('regrow' with the minutes it has stood scorched and how many it needs),
 *  it is ground again -- or a hedge, beside water: a hedge is what a burnt shore grows. */
export const Regrows = (into: number): Part => ({ name: 'Regrows', into, onregrow(c: Ctx) {
  const { world, thing: t, payload } = c;
  if (payload.minutes < payload.after) return false;
  let wetNear = false;
  for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) { const n = world.tileAt(t.x + dx, t.y + dy); if (n && has(n, 'Wet')) wetNear = true; }
  const next = wetNear && THORN_KIND >= 0 ? THORN_KIND : this.into;
  world.grid.set(t.x, t.y, next);
  return next;
} });
export let PUSH_KIND = -1;
export function setPushKind(k: number) { PUSH_KIND = k; }
/** Pushable: a block. Told 'push' with a direction, it moves one tile if the ground beyond will take it; what it
 *  leaves and what may stand there are the room's business, passed in as answers, not written here. */
export const Pushable = (): Part => ({ name: 'Pushable', onpush(c: Ctx) {
  const { thing: t, payload: p } = c;
  const nx = t.x + p.dx, ny = t.y + p.dy;
  if (!p.accepts(nx, ny)) return false;
  p.set(t.x, t.y, p.leaves(t.x, t.y));
  p.set(nx, ny, t.kind);
  return true;
} });
export let TIDAL_KINDS: [number, number] = [-1, -1];
export function setTidalKinds(water: number, shallow: number) { TIDAL_KINDS = [water, shallow]; }
/** Tidal: water that stands deep or shallow with the tide, from what it was at the world's making. */
export const Tidal = (base: number): Part => ({ name: 'Tidal', base, ontide(c: Ctx) {
  const want = tideKind(this.base, c.payload.tide);
  if (c.world.grid.at(c.thing.x, c.thing.y) !== want) c.payload.set(c.thing.x, c.thing.y, want);
  return want;
} });
export function setBrazierKind(k: number) { BRAZIER_KIND = k; }

/* THE BODIES AS THINGS: the player, the enemies and the pods on the screen, each a thing at its tile with the
 * parts its traits give it. Made afresh each time they are asked for; the bodies themselves keep their state. */
export function bodyThings(game, traitsOf, ox: number, oy: number, TS: number): Thing[] {
  const out: Thing[] = [];
  const at = (b) => [Math.floor((b.cx - ox) / TS), Math.floor((b.cy - oy) / TS)];
  const p = game.player;
  if (p) { const [x, y] = at(p); const t = thing(-2, x, y, wearing(game.state, 'mantle') ? [] : [Burns()]); (t as any).body = p; out.push(t); }   // the bramble mantle (LS-239): fire passes the wearer by
  for (const e of game.enemies || []) {
    if (e.dead) continue;
    const [x, y] = at(e); const parts: Part[] = [];
    const tr = traitsOf(e.type || e.kind);
    if (tr.burns) parts.push(Burns());
    if (tr.fearsFire) parts.push(Fears(tr.fearsFire));
    if (tr.drawnToLight) parts.push(DrawnToLight(tr.drawnToLight));
    const t = thing(-2, x, y, parts); (t as any).body = e; out.push(t);
  }
  for (const pod of game.pods || []) {
    if (pod.dead) continue;
    const [x, y] = at(pod); const t = thing(-3, x, y, [Fuse()]); (t as any).body = pod; out.push(t);
  }
  return out;
}
/** Every body standing in a fire is told so. */
export function standInFire(world: SimWorld, bodies: Thing[], payload: any) {
  for (const b of bodies) if (world.burning(b.x, b.y)) emit(world, 'standinfire', b, payload);
}
/** Ignite a tile through its parts; the answer is whether it lit. */
export function igniteTile(world: SimWorld, x: number, y: number, force: boolean, game: any): boolean {
  const t = world.tileAt(x, y);
  if (!t) return false;
  const r = emit(world, 'ignite', t, { force, game });
  return r === true;
}

/** Wet: it will not catch, and it damps what is beside it (drynessAt reads the table for that; here it answers heat). */
export const Wet = (): Part => ({ name: 'Wet', onheat: () => false });

/** Burnable: how readily it catches, how long it burns, what it leaves. */
export const Burnable = (flammable: number, fuel: number, ash: number | null, dry: boolean): Part => ({
  name: 'Burnable', flammable, fuel, ash, dry,
  onheat(c: Ctx) {
    const { world, thing: t, payload } = c;
    if (world.burning(t.x, t.y)) return false;
    const toward = 1 + 0.6 * (payload.dx * world.wind[0] + payload.dy * world.wind[1]);   // downwind catches sooner, upwind later
    const p = this.flammable * dryness(world, t.x, t.y) * toward * 0.35;
    const roll = world.roll(t.x, t.y);
    if (roll < p) { light(world, t.x, t.y, this.fuel || 1, t.kind); return true; }
    return false;
  },
  onburntout(c: Ctx) { if (this.ash !== null) c.world.grid.set(c.thing.x, c.thing.y, this.ash); },
  onblast(c: Ctx) { if (c.payload.handled) return; c.payload.handled = true; if (c.payload.ignite) c.payload.ignite(c.thing.x, c.thing.y); return true; },
  onburn(c: Ctx) { if (c.payload.ignite) c.payload.ignite(c.thing.x, c.thing.y); return true; },
  /* AN EMBER, A BLAST, A LIT BRAZIER: 'ignite' is heat with a will behind it. Forced, it lights whatever can
   * burn; otherwise a thing that is not wholly flammable rolls, with the roll systems.ts made (its tick, the
   * tile, and eleven), so an ember on the same hedge on the same frame lights or fails as it did. */
  onignite(c: Ctx) {
    const { world, thing: t, payload } = c;
    /* SOMETHING ELSE ON THIS TILE ANSWERED THE FLAME (2026-09-18): a brazier lights, and it does not also catch. Nothing
     * is returned rather than `false`, because in `emit` a false is a REFUSAL that ends the event and becomes its answer
     * -- and the ember's caller reads that answer: a refused ignite made `useEmber` flare a ground fire on the tile it
     * had just lit, which is the same bug wearing the other hat. */
    if (payload.handled) return;
    if (world.burning(t.x, t.y)) return false;
    if (!payload.force && this.flammable < 1) {
      const roll = ((mix(world.tick * 7 + t.x, t.y * 13 + 5, 11) >>> 8) & 1023) / 1024;
      if (roll >= this.flammable) return false;
    }
    light(world, t.x, t.y, this.fuel || 20, t.kind);
    return true;
  },
});
/** Lightable: a brazier. It lights and stays lit; it does not burn out. */
/* A BRAZIER LIGHTS; IT DOES NOT CATCH (2026-09-18). `MATTER[BRAZIER]` is flammable, so `tileParts` gave the tile
 * BOTH this part and `Burnable` -- and `emit` only stops on a REFUSAL, so lighting the bowl lit it and then the
 * burn part started a spreading ground fire on the same tile: fuel 0 read as `0 || 20`, twenty ticks of fire on a
 * brazier, heating its four neighbours and taking the grass round the one thing a player lights on purpose. The
 * 'blast' event already had this guard; 'ignite' has it now. */
export const Lightable = (): Part => ({ name: 'Lightable', onignite(c: Ctx) { if (c.payload.handled) return; c.payload.handled = true; if (c.payload.game && c.payload.game.lightBrazierAt) c.payload.game.lightBrazierAt(c.thing.x, c.thing.y); return true; },
  onblast(c: Ctx) { if (c.payload.handled) return; c.payload.handled = true; c.payload.game.lightBrazierAt(c.thing.x, c.thing.y); return true; },
  onburn(c: Ctx) { c.payload.game.lightBrazierAt(c.thing.x, c.thing.y); return true; } });
/** Burns: a body fire hurts. Read off the creature's traits; a wisp is made of light and has none. */
export const Burns = (): Part => ({ name: 'Burns', onstandinfire(c: Ctx) {
  const { thing: b, payload } = c, game = payload.game, body = (b as any).body;
  if (body === game.player) {
    if (body.submerged) return false;
    /* ...AND IT IS HEARD AND SEEN (2026-09-20). The tagged hurt takes the weather path, which flashes the bar and
     * makes no sound -- that path's own comment says the AFFLICTION sounds instead, and a fire on the ground is no
     * affliction. Standing in one took a heart every second in silence. Marked the way Scorched marks its bite. */
    const hp0 = game.state.hp;
    body.hurt(game, 1, body.cx, body.cy + 8, 'fire:' + payload.tick);
    if (game.state.hp < hp0) { if (game.sfx) game.sfx('burn', body.x); if (game.puff) game.puff(body.cx, body.cy - 6, '#ffb85c', 4, 'spark'); }
    return true;
  }   // TAGGED, LIKE THE CREATURES' (2026-09-17): untagged took the combat path -- a 72-frame invulnerability, hitstop and a shove -- so one lick of flame made the body proof against the next blow, and `kb = null` was there to undo a shove the tag never asks for
  if (body.dead || body.alight > 0) return false;   // a running torch is already burning; the fire does not stagger it again
  body.hurt(game, 1, body.cx, body.cy + 8, 'fire:' + payload.tick);
  if ((body.type === 'bramble' || body.type === 'bramblemother') && !body.dead && !(body.alight > 0)) { body.alight = payload.torchFrames; game.sfx('flame', body.x + 8); }   // the bramble mother is a hedge too (LS-225)
  return true;
} });
/** Fears: a body that keeps away from fire, by how much. Told 'urge' with the fires on the screen, it adds its
 *  push away from each one within four tiles to the payload's vector. */
export const Fears = (amount: number): Part => ({ name: 'Fears', amount, onurge(c: Ctx) {
  const { thing: b, payload } = c;
  for (const f of payload.fires) {
    const dx = b.x - f.x, dy = b.y - f.y, d = Math.hypot(dx, dy);
    if (d < 4 && d > 0) { payload.ux += (dx / d) * this.amount * (4 - d); payload.uy += (dy / d) * this.amount * (4 - d); }
  }
  /* AND A LIGHT KEEPS IT OFF AT NIGHT (LS-379, the night as the need). A lit
   * brazier and a carried torch are lights; by day a thing that fears fire
   * walks past them, and at night it will not come within three tiles and a
   * half. That ring is the first shelter a player has, and the only one until
   * they can plant a hedge: the night's pack circles the light and does not
   * enter it. A wisp is made of light and is DrawnToLight instead, which is
   * the exception that says the rule is a rule. */
  if (payload.night) {
    for (const l of payload.lights) {
      const dx = b.x - l.x, dy = b.y - l.y, d = Math.hypot(dx, dy);
      if (d < 3.5 && d > 0) { payload.ux += (dx / d) * this.amount * (3.5 - d) * 0.9; payload.uy += (dy / d) * this.amount * (3.5 - d) * 0.9; }
    }
  }
} });
/** DrawnToLight: a body that comes to a lit brazier at night, by how much. */
/** THE CINDER IS PULLED TO A FIRE (2026-09-18). `matter.ts` has said so since the trait was written -- "the cinder is
 *  pulled to a fire the way a wisp is to a light, by day or night, and closer" -- and the body's urge is built from
 *  its PARTS, of which there were only two: a thing that fears fire and a thing drawn to light. A cinder was handed an
 *  empty list and answered [0,0], so the one creature whose whole design is that it eats fire and grows never went to
 *  one. The old `urge()` in matter.ts was its only reader and nothing called it. */
export const EatsFire = (amount: number): Part => ({ name: 'EatsFire', amount, onurge(c: Ctx) {
  const { thing: b, payload } = c;
  for (const f of payload.fires) {
    const dx = f.x - b.x, dy = f.y - b.y, d = Math.hypot(dx, dy);
    if (d < 9 && d > 0.3) { payload.ux += (dx / d) * this.amount; payload.uy += (dy / d) * this.amount; }
  }
} });
export const DrawnToLight = (amount: number): Part => ({ name: 'DrawnToLight', amount, onurge(c: Ctx) {
  const { thing: b, payload } = c;
  if (!payload.night) return;
  for (const l of payload.lights) {
    const dx = l.x - b.x, dy = l.y - b.y, d = Math.hypot(dx, dy);
    if (d < 9 && d > 1.5) { payload.ux += (dx / d) * this.amount; payload.uy += (dy / d) * this.amount; }
  }
} });
/** What a body wants to do about its surroundings, asked of its parts: a vector in tiles, zero when nothing moves it. */
export function urgeOf(world: SimWorld, body: Thing, fires: { x: number; y: number }[], lights: { x: number; y: number }[], night: boolean): [number, number] {
  const payload = { fires, lights, night, ux: 0, uy: 0 };
  emit(world, 'urge', body, payload);
  return [payload.ux, payload.uy];
}
/** Fuse: a pod. Fire under it lights the fuse. */
export const Fuse = (): Part => ({ name: 'Fuse', onstandinfire(c: Ctx) { const pod = (c.thing as any).body; if (pod.dead || pod.fuse <= 1) return false; pod.fuse = 1; return true; } });

/** Fire: a thing of its own standing on a tile; it spends fuel, heats its four neighbours, and when spent tells the tile it burnt out. */
export const FirePart = (fuel: number, born: number, kind: number, struck = false): Part => ({
  name: 'Fire', fuel, born, kind, doused: false, struck,
  ontick(c: Ctx) {
    const { world, thing: f } = c;
    this.fuel--;
    // THE RAIN PUTS IT OUT (LS-223): a fire standing where the rain has soaked the ground is spent, whatever fuel it had
    // ...UNLESS THE LIGHTNING LIT IT (LS-381): a strike's fire holds four seconds in the downpour, the one fire the rain cannot start any other way
    if (world.fields && world.fields.rainWetAt(f.x, f.y) >= 0.6 && !(this.struck && world.tick - this.born < 240)) { this.fuel = 0; this.doused = true; }   // soaked ground: eight fire ticks of a downpour
    if (this.fuel <= 0) {
      if (this.doused) return 'spent';   // put out, not burnt out: the tile is as it was
      const tile = world.tileAt(f.x, f.y);
      if (tile) emit(world, 'burntout', tile);
      return 'spent';
    }
    for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
      const n = world.tileAt(f.x + dx, f.y + dy);
      if (!n) continue;
      emit(world, 'heat', n, { dx, dy, from: f });
    }
    return 'burning';
  },
});

export function light(world: SimWorld, x: number, y: number, fuel: number, kind: number) {
  const f = thing(-1, x, y, [FirePart(fuel, world.tick, kind)]);
  world.fires.push(f);
  return f;
}

/** Dryness, as `matter.ts` counts it: the biome, the hour, the wet things beside. */
export function dryness(world: SimWorld, x: number, y: number) {
  const here = world.tileAt(x, y);
  if (here && part(here, 'Burnable') && part(here, 'Burnable')!.dry) return 1;   // cut straw is dry in any weather
  let damp: number;
  if (world.fields) damp = world.fields.wetAt(x, y);   // THE WET FIELD (LS-223): what the water beside, and later the rain, left here
  else {
    let wetNear = 0;
    for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [1, -1], [-1, 1], [-1, -1]]) {
      const n = world.tileAt(x + dx, y + dy);
      if (n && has(n, 'Wet')) wetNear++;
    }
    damp = Math.min(1, wetNear * 0.2);
  }
  const noon = 1 - Math.abs(world.day - 0.3) * 1.4;
  return Math.max(0, Math.min(1, world.biomeDry * (0.6 + 0.4 * Math.max(0, noon)) - damp));
}

/* ONE TICK OF EVERY FIRE, THROUGH THE THINGS. The same signature as
 * `tickFire` and the same answer: the fires that caught and the fires that
 * were spent, in the order the old rule found them. Fires that catch during
 * the tick do not burn until the next, as before: the burning set is read
 * before the tick and the new fires are appended after. */
export function tickFireThings(grid: Grid, fires: Fire[], seedNum: number, tick: number, biomeDry: number,
  day: number, wind: [number, number], world: SimWorld | null = null): { caught: Fire[]; spent: Fire[]; world: SimWorld } {
  const w = world || new SimWorld(grid, seedNum);
  w.grid = grid; w.tick = tick; w.biomeDry = biomeDry; w.day = day; w.wind = wind;
  if (w.fields) { const win = w.window || undefined; w.fields.wetFromGrid(grid, win); w.fields.tickHeat(fires, win); }
  // the fires as things, in the caller's order; catches this tick are kept apart until the tick is over
  w.fires = fires.map((f) => { const t = thing(-1, f.x, f.y, [FirePart(f.fuel, f.born, f.kind ?? -1, !!(f as any).struck)]); (t as any).src = f; return t; });
  const before = w.fires.slice();
  const caught: Fire[] = [], spent: Fire[] = [];
  for (const f of before) {
    const nBefore = w.fires.length;
    const r = emit(w, 'tick', f);
    const fp = part(f, 'Fire')!;
    (f as any).src.fuel = fp.fuel;
    if (r === 'spent') { if (fp.doused) (f as any).src.doused = true; spent.push((f as any).src); }
    for (let i = nBefore; i < w.fires.length; i++) { const nf = w.fires[i], p = part(nf, 'Fire')!; caught.push({ x: nf.x, y: nf.y, fuel: p.fuel, born: p.born, kind: p.kind }); }
  }
  return { caught, spent, world: w };
}
